// SPEC-SOR-343 (R7/R12/R16) — the displayed-amount presenters, and the one
// money-text formatter every surface renders its figures through: the Review
// Fees panel (CourtFeesTab), the Filing Summary rail (SummaryV2), the
// order-summary rail and the Submit bar (OrderDetailsTab).
//
// Those surfaces read the legacy `fees` accumulator directly, so a draft whose
// quote was never resolved — or whose recalculation failed — still showed
// $0.00 or a stale figure beside a rail count the calculation had not priced.
// Everything they show now derives from the resolved record through
// `selectResolvedQuote` (the one read path, R13), via these presenters — kept
// dependency-light (barrel imports only, React-free, mirroring
// `fee-quote-record.ts`) so the spec property probes compile them directly:
//
//   feeAmountText — the exact text ANY surface renders for a figure. One
//     formatter, four surfaces: the R16 defect was the panel formatting its
//     own amounts with a bare `.toFixed(2)`, which diverges from the rail's
//     grouped text at $1,000 and up.
//   FEE_TOTAL_UNRESOLVED_LABEL — the one non-monetary state label a
//     non-resolved quote renders where its figure would be (R7).
//   presentFeeTotal — the record's total as the number and the exact text a
//     surface shows for it, or null when no figure may be shown.
//   presentFeeCount — the record's non-zero portions, or null.
//
// A resolved $0.00 total renders "$0.00" honestly — the prohibition is on
// figures while the quote is NOT resolved, and a waived filing's resolved
// total is $0.00 by FWR-11 (pinned by AX4, R1 and R2 of the spec).

import type { CourtFeesState } from '@lds-efsp/shared-contracts';
import { selectResolvedQuote } from './fee-quote-record';

/**
 * The exact text a surface renders for a fee figure — the ONE money formatter
 * behind every displayed fee amount (SPEC-SOR-343 R16): the panel's hero, the
 * panel's breakdown rows, the Filing Summary rail, the order summary and the
 * Submit bar all print this string for the same record, so the surfaces
 * cannot disagree at $1,000 and up (where ungrouped text diverges from
 * grouped).
 */
export function feeAmountText(amount: number): string {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** The state label every surface renders in place of its total while the quote is not resolved. */
export const FEE_TOTAL_UNRESOLVED_LABEL = 'Fees not yet calculated';

/** A resolved total and the exact text a surface shows for it. */
export interface FeeTotalPresentation {
  total: number;
  text: string;
}

/**
 * The record's total, ready to render — or null. The only way one of the three
 * surfaces obtains a fee figure (R7): null means the surface renders the state
 * label, never a number. `total` rides beside `text` so a consumer (and the
 * r12 property) can pin the text to the very number it claims to be.
 */
export function presentFeeTotal(courtFees: CourtFeesState): FeeTotalPresentation | null {
  const quote = selectResolvedQuote(courtFees);
  if (!quote) return null;
  return {
    total: quote.total,
    text: feeAmountText(quote.total),
  };
}

/**
 * The record's non-zero portions — or null while unresolved. A resolved $0.00
 * record (the waived-filing shape) counts zero portions, so the rail renders
 * no count line for it: the component keeps its `> 0` mount guard.
 */
export function presentFeeCount(courtFees: CourtFeesState): number | null {
  const quote = selectResolvedQuote(courtFees);
  if (!quote) return null;
  return [quote.court, quote.efm, quote.ldsService, quote.processingFee].filter(
    amount => amount > 0,
  ).length;
}
